import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { FiMenu, FiX, FiSearch } from 'react-icons/fi';
import { useLocation, useNavigate } from 'react-router-dom';
import SimpleThemeToggle from './SimpleThemeToggle';
import CommandPalette from './CommandPalette';

const HeaderContainer = styled.header`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  background: rgba(26, 26, 46, 0.8);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  transition: all 0.3s ease;
`;

const Nav = styled.nav`
  max-width: 1200px;
  margin: 0 auto;
  padding: 1rem 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const LogoContainer = styled(motion.div)`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
`;

const Logo = styled.img`
  width: 40px;
  height: 40px;
  filter: drop-shadow(0 4px 8px rgba(139, 92, 246, 0.3));
`;

const LogoText = styled(motion.span)`
  font-size: 1.5rem;
  font-weight: 700;
  background: linear-gradient(135deg, #8b5cf6, #ec4899);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const NavLinks = styled.div`
  display: flex;
  gap: 2rem;
  align-items: center;

  @media (max-width: 1024px) {
    display: none;
  }
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;

  @media (max-width: 1024px) {
    display: none;
  }
`;

const SearchButton = styled(motion.button)`
  background: rgba(139, 92, 246, 0.1);
  border: 1px solid rgba(139, 92, 246, 0.3);
  border-radius: 8px;
  padding: 0.5rem;
  color: #8b5cf6;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: rgba(139, 92, 246, 0.2);
    border-color: rgba(139, 92, 246, 0.5);
  }
`;

const NavLink = styled(motion.a)`
  color: #ffffff;
  text-decoration: none;
  font-weight: 500;
  position: relative;
  transition: color 0.3s ease;

  &:hover {
    color: #8b5cf6;
  }

  &::after {
    content: '';
    position: absolute;
    bottom: -5px;
    left: 0;
    width: 0;
    height: 2px;
    background: linear-gradient(135deg, #8b5cf6, #ec4899);
    transition: width 0.3s ease;
  }

  &:hover::after {
    width: 100%;
  }
`;

const MobileMenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  color: #ffffff;
  font-size: 1.5rem;
  cursor: pointer;

  @media (max-width: 1024px) {
    display: block;
  }
`;

const MobileMenu = styled(motion.div)`
  position: fixed;
  top: 64px;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(26, 26, 46, 0.98);
  backdrop-filter: blur(10px);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2rem;
  z-index: 1001;
  padding: 4rem 1rem 2rem;
  overflow-y: auto;
  height: calc(100vh - 64px); /* Ensures scrolling and visibility */
`;
// Moved the media query inside the MobileMenu styled component above, as it was outside and causing syntax errors.

const MobileNavLink = styled(motion.a)`
  color: #ffffff;
  text-decoration: none;
  font-size: 1.5rem;
  font-weight: 500;
  transition: color 0.3s ease;
  text-align: center;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  width: 100%;
  max-width: 300px;

  &:hover {
    color: #8b5cf6;
    background: rgba(139, 92, 246, 0.1);
  }

  @media (max-width: 768px) {
    font-size: 1.25rem;
    padding: 0.75rem 1rem;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 1.5rem;
  right: 1.5rem;
  background: none;
  border: none;
  color: #ffffff;
  font-size: 2rem;
  cursor: pointer;
  z-index: 1002;
  padding: 0.5rem;
  border-radius: 50%;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }
`;

const Header: React.FC = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isCommandPaletteOpen, setIsCommandPaletteOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (location.pathname === '/' && location.state && location.state.sectionId) {
      const el = document.getElementById(location.state.sectionId);
      if (el) {
        setTimeout(() => {
          el.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      }
    }
  }, [location]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setIsCommandPaletteOpen(true);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleNav = (sectionId: string) => {
    if (location.pathname === '/') {
      const el = document.getElementById(sectionId);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      navigate('/', { state: { sectionId } });
    }
    setIsMobileMenuOpen(false);
  };

  const logoVariants = {
    initial: { scale: 1 },
    hover: {
      scale: 1.1,
      rotate: [0, -10, 10, 0],
      transition: { duration: 0.6 }
    }
  };

  const letterVariants = {
    initial: { y: 0 },
    hover: {
      y: [-5, 5, -5],
      transition: {
        duration: 0.6,
        repeat: Infinity,
        repeatType: 'reverse' as const
      }
    }
  };

  return (
    <HeaderContainer>
      <Nav>
        <LogoContainer
          variants={logoVariants}
          initial="initial"
          whileHover="hover"
          onClick={() => handleNav('hero')}
        >
          <Logo src="/logo-pink.svg" alt="Briddy - Full-Stack Developer Logo" />
          <LogoText>
            <motion.span variants={letterVariants}>B</motion.span>
            <motion.span variants={letterVariants}>r</motion.span>
            <motion.span variants={letterVariants}>i</motion.span>
            <motion.span variants={letterVariants}>d</motion.span>
            <motion.span variants={letterVariants}>d</motion.span>
            <motion.span variants={letterVariants}>y</motion.span>
          </LogoText>
        </LogoContainer>
        <NavLinks>
          <NavLink href="#about" onClick={e => { e.preventDefault(); handleNav('about'); }}>About</NavLink>
          <NavLink href="#projects" onClick={e => { e.preventDefault(); handleNav('projects'); }}>Projects</NavLink>
          <NavLink href="#testimonials" onClick={e => { e.preventDefault(); handleNav('testimonials'); }}>Testimonials</NavLink>
          <NavLink href="#contact" onClick={e => { e.preventDefault(); handleNav('contact'); }}>Contact</NavLink>
          <NavLink href="/privacy-policy">Privacy Policy</NavLink>
          <NavLink href="/terms-of-service">Terms of Service</NavLink>
        </NavLinks>
        <HeaderActions>
          <SearchButton
            onClick={() => setIsCommandPaletteOpen(true)}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            title="Search (Ctrl+K)"
          >
            <FiSearch size={18} />
          </SearchButton>
          <SimpleThemeToggle />
        </HeaderActions>
        <MobileMenuButton onClick={() => setIsMobileMenuOpen(true)}>
          <FiMenu />
        </MobileMenuButton>
      </Nav>

      <AnimatePresence>
        {isMobileMenuOpen && (
          <MobileMenu
            initial={{ opacity: 0, x: 100 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 100 }}
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
          >
            <CloseButton onClick={() => setIsMobileMenuOpen(false)}>
              <FiX />
            </CloseButton>

            <MobileNavLink href="#about" onClick={e => { e.preventDefault(); handleNav('about'); }}>About</MobileNavLink>
            <MobileNavLink href="#projects" onClick={e => { e.preventDefault(); handleNav('projects'); }}>Projects</MobileNavLink>
            <MobileNavLink href="#testimonials" onClick={e => { e.preventDefault(); handleNav('testimonials'); }}>Testimonials</MobileNavLink>
            <MobileNavLink href="#contact" onClick={e => { e.preventDefault(); handleNav('contact'); }}>Contact</MobileNavLink>
            <MobileNavLink href="/privacy-policy">Privacy Policy</MobileNavLink>
            <MobileNavLink href="/terms-of-service">Terms of Service</MobileNavLink>
          </MobileMenu>
        )}
      </AnimatePresence>

      <CommandPalette
        isOpen={isCommandPaletteOpen}
        onClose={() => setIsCommandPaletteOpen(false)}
      />
    </HeaderContainer>
  );
};

export default Header;
